import { trigger, style, transition, animate } from '@angular/animations';
import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Flight, UserRole } from 'src/app/core/graphql/generated/gen-types';
import { ResponsiveService } from 'src/app/core/services/admin/responsive.service';
import { FlightService } from 'src/app/core/services/flight/flight.service';
import { environment } from 'src/environments/environment';

dayjs.extend(weekday);

@Component({
	selector: 'app-date-card',
	templateUrl: './date-card.component.html',
	styleUrls: ['./date-card.component.scss'],
	animations: [
		trigger('fadeSlideInOut', [
			transition(':enter', [
				style({ opacity: 0, transform: 'translateY(-20px)' }),
				animate('500ms', style({ opacity: 1, transform: 'translateY(0)' })),
			]),
			transition(':leave', [animate('250ms', style({ opacity: 0, transform: 'translateY(-20px)' }))]),
		]),
	],
})
export class DateCardComponent implements OnInit {
	@Input() operator: boolean;
	@Input() shareBookings: boolean;
	@Input() userRole: UserRole;
	@Input() flights: Flight[];
	@Input() date: string;

	// component guards
	guards = environment.componentGuards.dateCard;

	event: string;
	morningFlights: any[];
	eveningFlights: any[];
	today: boolean;
	showFlights = false;
	flightCount = 0;
	state: string = 'default';
	isXSmall = false;

	flightSubs: Subscription[] = [];
	private destroy$ = new Subject<void>();

	constructor(
		private router: Router,
		private responsiveService: ResponsiveService,
		private flightService: FlightService,
	) {}

	ngOnInit(): void {
		this.responsiveService.XSmallChanged.pipe(takeUntil(this.destroy$)).subscribe((isXSmall) => (this.isXSmall = isXSmall));
		this.today = dayjs(this.date).isSame(dayjs(), 'day');
		this.flightCount = this.flights.length;
		this.morningFlights = this.flights.filter((f) => f.period === 'MOR');
		this.eveningFlights = this.flights.filter((f) => f.period === 'EVE');
	}

	ngAfterViewInit(): void {
		this.flights.forEach((flight) => {
			this.flightService.getDashboard(flight.id).valueChanges.pipe(takeUntil(this.destroy$)).subscribe();
		});
	}

	ngOnDestroy(): void {
		this.flightSubs.forEach((sub) => sub.unsubscribe());
		this.destroy$.next();
		this.destroy$.complete();
	}

	ngOnChanges(): void {
		this.flightCount = this.flights.length;
		this.morningFlights = this.flights.filter((f) => f.period === 'MOR');
		this.eveningFlights = this.flights.filter((f) => f.period === 'EVE');
	}

	toggleFlights() {
		if (this.flightCount > 0) {
			this.showFlights = !this.showFlights;
			if (this.state === 'default') {
				this.state = 'disabled';
			} else {
				this.state = 'default';
			}
		}
	}

	plan() {
		// 026: pass the actual period instead of the historic hardcoded EVE — the daypart
		// with real (non-cancelled) flights wins, morning-only days deep-link to MOR
		const activeMorning = (this.morningFlights ?? []).filter((flight) => flight.status !== 'CAN');
		const activeEvening = (this.eveningFlights ?? []).filter((flight) => flight.status !== 'CAN');
		const period = activeMorning.length && !activeEvening.length ? 'MOR' : 'EVE';
		this.router.navigate([`/calendar/${this.date}/${period}`]);
	}

	getCardClass() {
		if (dayjs(this.date).weekday() >= 5) {
			return !this.isXSmall ? 'weekendday large' : 'weekendday handset';
		} else {
			return !this.isXSmall ? 'weekday large' : 'weekday handset';
		}
	}
}
